import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import { LayoutGrid, Users, FileText, Headphones, BarChart3, Radio, Webhook, Shield, Clock, BookOpen, LogOut, Phone, FileBarChart, Trophy } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const menuConfig = {
  "super-admin": [
    { key: "nav.dashboard", icon: LayoutGrid, path: "/admin" },
    { key: "leaderboard.title", icon: Trophy, path: "/leaderboard" },
    { key: "nav.users", icon: Users, path: "/admin", tab: "users" },
    { key: "nav.scripts", icon: FileText, path: "/admin", tab: "scripts" },
    { key: "reporting.title", icon: FileBarChart, path: "/reports" },
    { key: "nav.operators", icon: Headphones, path: "/operator" },
    { key: "nav.analytics", icon: BarChart3, path: "/client" },
    { key: "nav.system_audit", icon: Shield, path: "/audit" },
  ],
  admin: [
    { key: "nav.dashboard", icon: LayoutGrid, path: "/admin" },
    { key: "leaderboard.title", icon: Trophy, path: "/leaderboard" },
    { key: "nav.scripts", icon: FileText, path: "/admin", tab: "scripts" },
    { key: "reporting.title", icon: FileBarChart, path: "/reports" },
    { key: "nav.operators", icon: Headphones, path: "/operator" },
    { key: "nav.analytics", icon: BarChart3, path: "/client" },
  ],
  operator: [
    { key: "nav.workspace", icon: Headphones, path: "/operator" },
    { key: "nav.knowledge_base", icon: BookOpen, path: "/operator", tab: "kb" },
    { key: "nav.time_tracking", icon: Clock, path: "/operator", tab: "time" },
    { key: "nav.softphone", icon: Phone, path: "/operator", tab: "phone" },
  ],
  client: [
    { key: "nav.analytics", icon: BarChart3, path: "/client" },
    { key: "reporting.title", icon: FileBarChart, path: "/reports" },
    { key: "nav.recordings", icon: Radio, path: "/client", tab: "recordings" },
    { key: "nav.webhooks", icon: Webhook, path: "/client", tab: "webhooks" },
  ],
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) return null;
  const items = menuConfig[user.role] || menuConfig.operator;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <aside className="w-60 h-screen bg-white border-r border-[#E5E7EB] flex flex-col shrink-0" data-testid="sidebar">
      <div className="p-5 border-b border-[#E5E7EB]">
        <h1
          className="text-lg font-black tracking-tight text-[#0A0A0A] cursor-pointer"
          onClick={() => navigate("/")}
          data-testid="sidebar-logo"
        >
          Talk2Support
        </h1>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#9CA3AF] font-bold mt-0.5">
          {user.role.replace("-", " ")}
        </p>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto thin-scrollbar">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.key}
              data-testid={`sidebar-${item.key.split(".").pop()}`}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-all duration-200
                ${isActive
                  ? "text-[#002FA7] bg-[#002FA7]/5 border-r-2 border-[#002FA7] font-semibold"
                  : "text-[#4B5563] hover:text-[#0A0A0A] hover:bg-[#F8F9FA]"
                }`}
            >
              <Icon size={18} strokeWidth={isActive ? 2.5 : 1.5} />
              <span>{t(item.key)}</span>
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-[#E5E7EB] space-y-3">
        <LanguageToggle />
        <div className="flex items-center gap-2.5">
          <Avatar className="h-8 w-8 rounded-none">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="rounded-none bg-[#002FA7] text-white text-xs font-bold">
              {user.name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#0A0A0A] truncate">{user.name}</p>
            <p className="text-[10px] text-[#9CA3AF] truncate">{user.email}</p>
          </div>
          <button
            data-testid="sidebar-logout"
            onClick={handleLogout}
            className="p-1.5 text-[#9CA3AF] hover:text-[#FF2A2A] transition-colors"
            title={t("common.sign_out")}
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
